<template>
  <HrPageLayout
    title="Last Expense"
    subtitle="Manage and track last expense claims and payments"
    icon="🕯"
    :breadcrumb-items="[{ label: 'Home', route: '/dashboard' }, { label: 'Claims & Payments' }, { label: 'Last Expense' }]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" @click="loadClaims">
        <i class="bx bx-refresh"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="openSubmitModal">
        <i class="bx bx-plus"></i> Submit Claim
      </button>
    </template>

    <div class="layout-content">
      <div class="page-wrapper">
        <!-- Stats Cards -->
        <div class="stats-grid">
          <!-- Total Claims -->
          <div class="stat-card stat-primary">
            <div class="stat-icon">
              <span class="icon">📁</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Total Claims</div>
              <div class="stat-value">{{ claims.length }}</div>
              <div class="stat-subtext">All submitted claims</div>
            </div>
          </div>

          <!-- Pending Approval -->
          <div class="stat-card stat-warning">
            <div class="stat-icon">
              <span class="icon">⏱️</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Pending</div>
              <div class="stat-value">{{ summary.pending }}</div>
              <div class="stat-subtext warning">Awaiting approval</div>
            </div>
          </div>

          <!-- Approved -->
          <div class="stat-card stat-info">
            <div class="stat-icon">
              <span class="icon">✓</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Approved</div>
              <div class="stat-value">{{ summary.approved }}</div>
              <div class="stat-subtext">Ready for payment</div>
            </div>
          </div>

          <!-- Paid -->
          <div class="stat-card stat-success">
            <div class="stat-icon">
              <span class="icon">💰</span>
            </div>
            <div class="stat-content">
              <div class="stat-title">Paid</div>
              <div class="stat-value">{{ summary.paid }}</div>
              <div class="stat-subtext success">Total: KES {{ formatNum(summary.totalPaidAmount) }}</div>
            </div>
          </div>
        </div>

        <div class="filters-card">
          <div class="filters-header">
            <h3 class="filters-title">Filter Claims</h3>
          </div>
          <div class="filters-grid">
            <div class="filter-group">
              <label class="filter-label">Status</label>
              <select v-model="filterStatus" class="filter-select" @change="loadClaims">
                <option value="">All statuses</option>
                <option value="Submitted">Submitted</option>
                <option value="Approved">Approved</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
            <div class="filter-group">
              <label class="filter-label">Search</label>
              <div class="filter-input-wrap">
                <i class="bx bx-search filter-icon"></i>
                <input type="text" class="filter-input" placeholder="Search claimant or deceased..." v-model="searchQuery" @input="filterClaims">
              </div>
            </div>
          </div>
        </div>

        <div class="table-wrap card-section">
          <table class="table table-bordered table-sm modern-table">
            <thead>
              <tr>
                <th>Deceased</th>
                <th>Department</th>
                <th>Claimant</th>
                <th>Relationship</th>
                <th class="text-end">Amount (KES)</th>
                <th>Death date</th>
                <th class="text-center">Program</th>
                <th class="text-center">Status</th>
                <th>Payment Ref</th>
                <th class="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in displayedClaims" :key="c.id" class="table-row-hover">
                <td>
                  <div class="fw-bold text-dark">{{ c.deceased_name || '–' }}</div>
                  <div class="text-xs text-muted">ID: {{ c.deceased_national_id || '–' }}</div>
                </td>
                <td>{{ c.department_id }}</td>
                <td>
                  <div class="fw-bold text-dark">{{ c.claimant_name }}</div>
                  <div class="text-xs text-muted">{{ c.claimant_phone || '–' }}</div>
                </td>
                <td><span class="badge-relationship">{{ c.claimant_relationship || '–' }}</span></td>
                <td class="text-end fw-bold text-dark">{{ formatNum(c.claim_amount) }}</td>
                <td>{{ c.death_date || '–' }}</td>
                <td class="text-center">
                  <a v-if="c.funeral_program_url" :href="getAttachmentUrl(c.funeral_program_url)" target="_blank" class="action-icon view-doc" title="View Program">
                    <i class="mdi mdi-file-document-outline"></i>
                  </a>
                  <span v-else class="text-muted">–</span>
                </td>
                <td class="text-center">
                  <span class="status-badge" :class="getStatusClass(c.status)">{{ c.status }}</span>
                </td>
                <td>{{ c.payment_reference || '–' }}</td>
                <td class="text-end">
                  <div class="action-buttons">
                    <button v-if="c.status === 'Submitted'" type="button" class="btn-action approve" @click="approveClaim(c.id)" title="Approve">
                      <i class="mdi mdi-check-decagram"></i> Approve
                    </button>
                    <button v-if="c.status === 'Approved'" type="button" class="btn-action pay" @click="openPaymentModal(c)" title="Record Payment">
                      <i class="mdi mdi-cash-register"></i> Pay
                    </button>
                  </div>
                </td>
              </tr>
              <tr v-if="!displayedClaims.length">
                <td colspan="10" class="empty-state">
                  <div class="empty-state-content">
                    <i class="mdi mdi-inbox-remove"></i>
                    <p>No claims found matching your criteria</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Submit Claim Modal -->
    <div class="modal-overlay" v-if="showSubmitModal" @click.self="showSubmitModal = false">
      <div class="modal-dialog-custom" style="max-width: 800px;">
        <div class="modal-content premium-modal">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="mdi mdi-file-document-edit text-primary me-2"></i>
              Submit Last Expense Claim
            </h5>
            <button type="button" class="custom-close" @click="showSubmitModal = false">
              <i class="bx bx-x"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-12">
                <label class="form-label fw-bold">Select Deceased Employee</label>
                <div class="search-box-wrapper mb-2">
                  <i class="bx bx-search search-icon"></i>
                  <input type="text" v-model="employeeSearch" class="form-control ps-5" placeholder="Search employee by name or ID...">
                </div>
                <select v-model="selectedEmployeeId" class="form-select" @change="onEmployeeSelect">
                  <option value="">-- Select Employee --</option>
                  <option v-for="emp in filteredEmployees" :key="emp.id" :value="emp.id">
                    {{ emp.firstName }} {{ emp.lastName }} ({{ emp.nationalId }})
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label">Deceased Name</label>
                <input v-model="claimForm.deceased_name" type="text" class="form-control bg-light" readonly>
              </div>
              <div class="col-md-6">
                <label class="form-label">Deceased National ID</label>
                <input v-model="claimForm.deceased_national_id" type="text" class="form-control bg-light" readonly>
              </div>
              <div class="col-md-12">
                <label class="form-label">Department</label>
                <select v-model="claimForm.department_id" class="form-select bg-light" disabled>
                  <option value="">Select</option>
                  <option v-for="d in departments" :key="d.id" :value="d.departmentID || d.id">{{ d.departmentName }}</option>
                </select>
              </div>
              
              <div class="col-12 mt-4 pt-3 border-top position-relative">
                <h6 class="fw-bold text-primary mb-3">Claimant Details</h6>
                <div class="alert alert-info py-2 px-3 text-sm d-flex align-items-center gap-2" v-if="selectedEmployeeId">
                  <i class="mdi mdi-information-outline"></i>
                  Select a registered Next of Kin to receive the funds.
                </div>
                
                <label class="form-label">Who gets the funds?</label>
                <div v-if="selectedEmployeeId">
                  <select v-model="claimForm.fund_recipient" class="form-select" @change="onKinSelect">
                    <option value="">-- Select Next of Kin --</option>
                    <option v-for="(kin, idx) in nextOfKins" :key="idx" :value="kin.name">
                      {{ kin.name }} ({{ kin.relationship }})
                    </option>
                  </select>
                  <div v-if="nextOfKins.length === 0" class="text-danger mt-2 text-sm d-flex align-items-center gap-1">
                    <i class="mdi mdi-alert-circle-outline"></i>
                    No next of kin found. Please add one in the employee profile.
                  </div>
                </div>
                <div v-else class="text-muted mt-1 text-sm italic">
                  Select an employee first.
                </div>
              </div>

              <div class="col-md-6">
                <label class="form-label">Claimant Name <span class="text-danger">*</span></label>
                <input v-model="claimForm.claimant_name" type="text" class="form-control" required>
              </div>
              <div class="col-md-6">
                <label class="form-label">Relationship</label>
                <input v-model="claimForm.claimant_relationship" type="text" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">Claimant Phone</label>
                <input v-model="claimForm.claimant_phone" type="text" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">Claim Amount (KES)</label>
                <div class="input-group">
                  <span class="input-group-text">KES</span>
                  <input v-model.number="claimForm.claim_amount" type="number" step="0.01" class="form-control fw-bold text-dark">
                </div>
              </div>
              <div class="col-md-6">
                <label class="form-label">Death Date</label>
                <input v-model="claimForm.death_date" type="date" class="form-control">
              </div>
              <div class="col-md-6">
                <label class="form-label">Funeral Program Upload</label>
                <input type="file" @change="onProgramUpload" accept=".pdf,.doc,.docx,.jpg,.png" class="form-control">
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showSubmitModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="submitClaim">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Submit Claim
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Record Payment Modal -->
    <div class="modal-overlay" v-if="showPaymentModal" @click.self="showPaymentModal = false">
      <div class="modal-dialog-custom" style="max-width: 500px;">
        <div class="modal-content premium-modal">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="mdi mdi-cash-register text-success me-2"></i>
              Record Payment
            </h5>
            <button type="button" class="custom-close" @click="showPaymentModal = false">
              <i class="bx bx-x"></i>
            </button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Payment Reference / Cheque No.</label>
              <input v-model="paymentForm.payment_reference" type="text" class="form-control form-control-lg" placeholder="e.g. TR-10924">
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">Payment Date</label>
              <input v-model="paymentForm.payment_date" type="date" class="form-control">
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showPaymentModal = false">Cancel</button>
            <button type="button" class="hr-btn hr-btn-success" :disabled="saving" @click="savePayment">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              Confirm Payment
            </button>
          </div>
        </div>
      </div>
    </div>

  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl } from "@/api";

export default {
  name: "LastExpense",
  components: { HrPageLayout },
  data() {
    return {
      claims: [],
      displayedClaims: [],
      searchQuery: '',
      filterStatus: '',
      showSubmitModal: false,
      showPaymentModal: false,
      saving: false,
      departments: [],
      allEmployees: [],
      filteredEmployees: [],
      employeeSearch: '',
      selectedEmployeeId: '',
      nextOfKins: [],
      
      claimForm: {
        deceased_name: '', deceased_national_id: '', department_id: '',
        claimant_name: '', claimant_relationship: '', claimant_phone: '',
        claim_amount: 0, death_date: '', funeral_program: null, fund_recipient: ''
      },
      paymentForm: { id: '', payment_reference: '', payment_date: '' }
    };
  },
  computed: {
    summary() {
      let pending = 0, approved = 0, paid = 0, totalPaidAmount = 0;
      this.claims.forEach(c => {
        if (c.status === 'Submitted') pending++;
        else if (c.status === 'Approved') approved++;
        else if (c.status === 'Paid') {
          paid++;
          totalPaidAmount += parseFloat(c.claim_amount) || 0;
        }
      });
      return { pending, approved, paid, totalPaidAmount };
    }
  },
  mounted() {
    this.loadClaims();
    this.getDepartments();
    this.getEmployees();
  },
  methods: {
    getEmployees() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success) {
          this.allEmployees = res.data.data.employees || [];
          this.filteredEmployees = this.allEmployees.slice(0, 10);
        }
      }).catch(() => {});
    },
    onEmployeeSelect() {
      this.nextOfKins = [];
      this.claimForm.fund_recipient = "";
      this.claimForm.claimant_name = "";
      this.claimForm.claimant_relationship = "";
      this.claimForm.claimant_phone = "";
      
      const emp = this.allEmployees.find(e => e.id === this.selectedEmployeeId);
      if (emp) {
        this.claimForm.deceased_name = emp.firstName + ' ' + (emp.lastName || '');
        this.claimForm.deceased_national_id = emp.nationalId || '';
        this.claimForm.department_id = emp.departmentID || '';
        
        const fd = new FormData();
        fd.append("function", "getEmployeeProfile");
        fd.append("employeeId", emp.id);
        execute(fd, authUrl).then((res) => {
          if (res.data && res.data.success) {
            this.nextOfKins = res.data.data.nextOfKin || [];
          }
        }).catch(() => {});
      } else {
        this.claimForm.deceased_name = "";
        this.claimForm.deceased_national_id = "";
        this.claimForm.department_id = "";
      }
    },
    onKinSelect() {
      const kin = this.nextOfKins.find(k => k.name === this.claimForm.fund_recipient);
      if (kin) {
        this.claimForm.claimant_name = kin.name;
        this.claimForm.claimant_relationship = kin.relationship || '';
        this.claimForm.claimant_phone = kin.phone || kin.phoneNumber || '';
      }
    },
    onProgramUpload(e) {
      if (e.target.files && e.target.files[0]) {
        this.claimForm.funeral_program = e.target.files[0];
      }
    },
    getAttachmentUrl(path) {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      const baseUrl = authUrl.replace(/authentication\/index\.php$/, '');
      return baseUrl + path;
    },
    getDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.departments)
          this.departments = res.data.data.departments;
      }).catch(() => {});
    },
    loadClaims() {
      const fd = new FormData();
      fd.append("function", "getLastExpenseClaims");
      execute(fd, authUrl).then((res) => {
        this.claims = (res.data && res.data.success && res.data.data && res.data.data.claims) ? res.data.data.claims : [];
        this.filterClaims();
      }).catch(() => { this.claims = []; });
    },
    filterClaims() {
      this.displayedClaims = this.claims.filter(c => {
        const matchesStatus = !this.filterStatus || c.status === this.filterStatus;
        const q = this.searchQuery.toLowerCase();
        const matchesSearch = !q || 
          (c.claimant_name || '').toLowerCase().includes(q) || 
          (c.deceased_name || '').toLowerCase().includes(q) ||
          (c.deceased_national_id || '').toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
      });
    },
    formatNum(n) { return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); },
    getStatusClass(status) {
      switch (status) {
        case 'Submitted': return 'badge-warning';
        case 'Approved': return 'badge-info';
        case 'Paid': return 'badge-success';
        case 'Rejected': return 'badge-danger';
        default: return 'badge-secondary';
      }
    },
    openSubmitModal() {
      this.selectedEmployeeId = "";
      this.employeeSearch = "";
      this.nextOfKins = [];
      this.claimForm = { deceased_name: "", deceased_national_id: "", department_id: "", claimant_name: "", claimant_relationship: "", claimant_phone: "", claim_amount: 0, death_date: "", fund_recipient: "", funeral_program: null };
      this.showSubmitModal = true;
    },
    submitClaim() {
      if (!this.claimForm.claimant_name) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "submitLastExpenseClaim");
      Object.keys(this.claimForm).forEach((k) => {
        if (k === 'funeral_program') {
          if (this.claimForm[k]) fd.append('funeral_program', this.claimForm[k]);
        } else {
          fd.append(k, this.claimForm[k] == null ? "" : this.claimForm[k]);
        }
      });
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showSubmitModal = false;
          this.loadClaims();
        } else {
          alert(res.data && res.data.message ? res.data.message : "Submit failed");
        }
      }).catch(() => { this.saving = false; });
    },
    approveClaim(id) {
      if (!confirm("Are you sure you want to approve this claim?")) return;
      const fd = new FormData();
      fd.append("function", "approveLastExpenseClaim");
      fd.append("id", id);
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success) this.loadClaims();
        else alert(res.data && res.data.message ? res.data.message : "Approval failed");
      }).catch(() => {});
    },
    openPaymentModal(c) {
      this.paymentForm = { id: c.id, payment_reference: c.payment_reference || "", payment_date: c.payment_date || new Date().toISOString().slice(0, 10) };
      this.showPaymentModal = true;
    },
    savePayment() {
      if (!this.paymentForm.id) return;
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "updateLastExpensePayment");
      fd.append("id", this.paymentForm.id);
      fd.append("payment_reference", this.paymentForm.payment_reference);
      fd.append("payment_date", this.paymentForm.payment_date);
      execute(fd, authUrl).then((res) => {
        this.saving = false;
        if (res.data && res.data.success) {
          this.showPaymentModal = false;
          this.loadClaims();
        } else {
          alert(res.data && res.data.message ? res.data.message : "Save failed");
        }
      }).catch(() => { this.saving = false; });
    }
  }
};
</script>

<style scoped>
/* Stats Grid matching HrTransfers */
.stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1.25rem; margin-bottom: 1.5rem; }
.stat-card { border-radius: 12px; padding: 1.25rem; display: flex; align-items: flex-start; gap: 1rem; border: 1px solid rgba(0, 0, 0, 0.05); background: #fff; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02); transition: transform 0.2s, box-shadow 0.2s; }
.stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 12px rgba(0, 0, 0, 0.05); }
.stat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.5rem; flex-shrink: 0; }
.stat-content { flex: 1; }
.stat-title { font-size: 0.875rem; font-weight: 600; color: #64748b; margin-bottom: 0.25rem; text-transform: uppercase; letter-spacing: 0.5px; }
.stat-value { font-size: 1.5rem; font-weight: 700; color: #1e293b; line-height: 1.2; margin-bottom: 0.25rem; }
.stat-subtext { font-size: 0.75rem; color: #64748b; }
.stat-primary .stat-icon { background: #e0e7ff; color: #4f46e5; }
.stat-warning .stat-icon { background: #fef3c7; color: #d97706; }
.stat-warning .stat-subtext.warning { color: #d97706; font-weight: 500; }
.stat-info .stat-icon { background: #e0f2fe; color: #0284c7; }
.stat-success .stat-icon { background: #dcfce7; color: #16a34a; }
.stat-success .stat-subtext.success { color: #16a34a; font-weight: 500; }
.stat-danger .stat-icon { background: #fee2e2; color: #dc2626; }
.stat-danger .stat-subtext.danger { color: #dc2626; font-weight: 500; }

/* Filters */
.filters-card { background: #fff; border-radius: 12px; padding: 1.25rem; margin-bottom: 1.5rem; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); border: 1px solid rgba(0, 0, 0, 0.05); }
.filters-header { margin-bottom: 1rem; border-bottom: 1px solid #e2e8f0; padding-bottom: 0.75rem; }
.filters-title { font-size: 1rem; font-weight: 600; color: #1e293b; margin: 0; }
.filters-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 1rem; }
.filter-group { display: flex; flex-direction: column; gap: 0.375rem; }
.filter-label { font-size: 0.813rem; font-weight: 600; color: #475569; }
.filter-select, .filter-input { width: 100%; padding: 0.5rem 0.75rem; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 0.875rem; color: #1e293b; transition: all 0.2s; }
.filter-select:focus, .filter-input:focus { outline: none; border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
.filter-input-wrap { position: relative; }
.filter-icon { position: absolute; left: 0.75rem; top: 50%; transform: translateY(-50%); color: #94a3b8; }
.filter-input-wrap .filter-input { padding-left: 2.25rem; }

/* Modern Table */
.card-section { background: #fff; border-radius: 12px; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05); border: 1px solid rgba(0, 0, 0, 0.05); overflow: hidden; padding: 0; }
.table-wrap { overflow-x: auto; }
.modern-table { width: 100%; margin: 0; border-collapse: separate; border-spacing: 0; }
.modern-table th { background: #f8fafc; color: #475569; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.5px; padding: 1rem; border-bottom: 1px solid #e2e8f0; white-space: nowrap; }
.modern-table td { padding: 1rem; border-bottom: 1px solid #e2e8f0; vertical-align: middle; font-size: 0.875rem; }
.table-row-hover:hover { background: #f8fafc; }
.text-xs { font-size: 0.75rem; }
.fw-bold { font-weight: 600; }

/* Badges */
.status-badge { display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; }
.badge-warning { background: #fef3c7; color: #d97706; }
.badge-info { background: #e0f2fe; color: #0284c7; }
.badge-success { background: #dcfce7; color: #16a34a; }
.badge-secondary { background: #f1f5f9; color: #64748b; }
.badge-relationship { background: #f1f5f9; color: #475569; padding: 0.2rem 0.5rem; border-radius: 4px; font-size: 0.75rem; font-weight: 500; }

/* Actions */
.action-buttons { display: flex; gap: 0.5rem; justify-content: flex-end; }
.btn-action { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.375rem 0.75rem; border-radius: 6px; font-size: 0.75rem; font-weight: 500; border: none; cursor: pointer; transition: all 0.2s; }
.btn-action.approve { background: #dcfce7; color: #16a34a; }
.btn-action.approve:hover { background: #bbf7d0; }
.btn-action.pay { background: #e0f2fe; color: #0284c7; }
.btn-action.pay:hover { background: #bae6fd; }
.action-icon { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; color: #475569; background: #f1f5f9; transition: all 0.2s; font-size: 1.1rem; }
.action-icon.view-doc:hover { background: #e0e7ff; color: #4f46e5; }

/* Empty State */
.empty-state { padding: 3rem 1rem !important; text-align: center; }
.empty-state-content { display: flex; flex-direction: column; align-items: center; gap: 0.5rem; color: #94a3b8; }
.empty-state-content i { font-size: 3rem; opacity: 0.5; }

/* Modal styling overrides */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 1.5rem;
  overflow-y: auto;
}

.modal-dialog-custom {
  width: 100%;
  margin: auto;
  animation: modalFadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: translateY(20px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

.premium-modal {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.premium-modal .modal-header {
  padding: 1.5rem 1.75rem;
  border-bottom: 1px solid #f1f5f9;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.premium-modal .modal-title {
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
}

.custom-close {
  background: #f1f5f9;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.custom-close:hover {
  background: #e2e8f0;
  color: #0f172a;
  transform: rotate(90deg);
}

.premium-modal .modal-body {
  padding: 1.75rem;
  overflow-y: auto;
  max-height: calc(100vh - 220px);
  background: #f8fafc;
}

.premium-modal .modal-footer {
  padding: 1.25rem 1.75rem;
  border-top: 1px solid #f1f5f9;
  background: #ffffff;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* Form Polish inside modal */
.search-box-wrapper { position: relative; }
.search-box-wrapper .search-icon { position: absolute; left: 1rem; top: 50%; transform: translateY(-50%); color: #64748b; font-size: 1.25rem; }
.search-box-wrapper .form-control { border-radius: 8px; padding-top: 0.6rem; padding-bottom: 0.6rem; border-color: #cbd5e1; }
.form-label { font-size: 0.875rem; color: #334155; font-weight: 600; margin-bottom: 0.375rem; }
.form-control, .form-select { border-radius: 8px; border-color: #cbd5e1; padding: 0.6rem 0.75rem; transition: all 0.2s; box-shadow: 0 1px 2px rgba(0,0,0,0.02); }
.form-control:focus, .form-select:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1); }
.form-control.bg-light, .form-select.bg-light { background-color: #f1f5f9 !important; border-color: #e2e8f0; color: #64748b; }
.input-group-text { background: #f1f5f9; border-color: #cbd5e1; color: #475569; font-weight: 600; border-radius: 8px 0 0 8px; }
.input-group .form-control { border-radius: 0 8px 8px 0; }
.alert-info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e3a8a; border-radius: 8px; }
</style>
